import { useEffect, useRef, useState } from 'preact/hooks';

import cn from 'clsx';

import { WalletType } from '@/shared/api/user/types';
import { formatBigNumber } from '@/shared/helpers/format-big-number';
import { useTimer } from '@/shared/hooks/use-timer';
import { useAppActions, useWallet } from '@/shared/stores/app-store/app-store';
import { Lottie } from '@/shared/ui/lottie';

import experienceLottie from '@/assets/lotties/xp.lottie?url';

import s from './index.module.scss';

const ANIMATION_INTERVAL_MS = 10000; // 10s

export const XpIcon = () => {
  const { fetchUserProfile } = useAppActions();

  const xpWallet = useWallet(WalletType.XP);
  const playcoinWallet = useWallet(WalletType.PLAYCOIN);

  const { balance, level, activeBooster: XPActiveBooster } = xpWallet ?? {};
  const { activeBooster: playcoinActiveBooster } = playcoinWallet ?? {};

  const boosterXPValue = XPActiveBooster?.value ?? 1;
  const boosterXPActiveTo = XPActiveBooster?.activeTo;
  const hasBoosterXP = boosterXPValue > 1;

  const boosterPlaycoinValue = playcoinActiveBooster?.value ?? 1;
  const boosterPlaycoinActiveTo = playcoinActiveBooster?.activeTo;
  const hasBoosterPlaycoin = boosterPlaycoinValue > 1;

  const [levelUpActive, setLevelUpActive] = useState(false);

  // Refetch wallet when booster has expired
  useTimer(boosterXPActiveTo, () => {
    // Refetch wallet when booster has expired with 1s timeout to wait server updates
    // avoiding extra fetch when play coin booster is active
    if (
      boosterXPActiveTo &&
      hasBoosterXP &&
      !(boosterPlaycoinActiveTo && hasBoosterPlaycoin)
    ) {
      setTimeout(() => {
        fetchUserProfile();
      }, 1000);
    }
  });

  const levelRef = useRef(level);

  const lottieAnimationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const levelUpAnimationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    lottieAnimationIntervalRef.current = setInterval(() => {
      setIsAnimating(true);
    }, ANIMATION_INTERVAL_MS);

    return () => {
      if (lottieAnimationIntervalRef.current)
        clearInterval(lottieAnimationIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    // Skip level up animation on the initial render
    levelRef.current = level;

    if (levelRef.current !== level && levelRef.current !== undefined) {
      setLevelUpActive(true);

      levelUpAnimationIntervalRef.current = setInterval(() => {
        setLevelUpActive(false);
      }, 2000);

      return () => {
        if (levelUpAnimationIntervalRef.current)
          clearInterval(levelUpAnimationIntervalRef.current);
      };
    }
  }, [level]);

  return (
    <div className={cn(s.container, hasBoosterXP && s.boosted)}>
      <div className={s['content']}>
        <div className={s['lottie-wrapper']}>
          <Lottie
            src={experienceLottie}
            isPlaying={isAnimating}
            loop={false}
            resetOnResume
            onComplete={() => setIsAnimating(false)}
            className={s.icon}
          />
        </div>
        <span className={s['xp-value']}>{formatBigNumber(balance) ?? 0}</span>
        {hasBoosterXP && <span className={s.booster}>X{boosterXPValue}</span>}
      </div>

      {levelUpActive && (
        <div className={s['level-up']}>
          <span className={s['level-up-text']}>lvl up!</span>
        </div>
      )}
    </div>
  );
};
